import React, { useState, useEffect } from 'react';
import axios from 'axios';

function SummaryTabUser({ grades, evidence, discipline }) {
  const [displayType, setDisplayType] = useState('standard');
  const [categorySpans, setCategorySpans] = useState([]);

  const normalizeLevel = (level) => {
    const normalized = String(level || '').trim().toLowerCase();
    if (!normalized) return null;
    if (normalized === 'explore' || normalized === 'e') return 'Explore';
    if (normalized === 'apply' || normalized === 'a') return 'Apply';
    if (
      normalized === 'independently implement' ||
      normalized === 'independent implement' ||
      normalized === 'i'
    ) {
      return 'Independently Implement';
    }
    return null;
  };

  const objectiveIdMatches = (evidenceObjectiveId, objectiveId) => {
    if (evidenceObjectiveId === objectiveId) return true;
    return String(evidenceObjectiveId) === String(objectiveId);
  };

  // Load discipline config to check display type and get category spans
  useEffect(() => {
    const loadDisciplineConfig = async () => {
      try {
        const response = await axios.get(`/outcomes/api/discipline-configs/${discipline}`);
        setDisplayType(response.data?.display_type || 'standard');
        
        // Parse gradeband_spans if category display type
        if (response.data?.display_type === 'category' && response.data?.gradeband_spans) {
          try {
            const spans = JSON.parse(response.data.gradeband_spans);
            setCategorySpans(spans);
          } catch (e) {
            console.error('Failed to parse gradeband_spans:', e);
          }
        }
      } catch (error) {
        console.error('Failed to load discipline config:', error);
      }
    };
    loadDisciplineConfig();
  }, [discipline]);

  // Count evidence by objective and level
  const getEvidenceCounts = (objectiveId) => {
    const counts = {
      Explore: 0,
      Apply: 0,
      'Independently Implement': 0
    };

    if (!evidence) return counts;

    evidence.forEach(ev => {
      if (objectiveIdMatches(ev.objective_id, objectiveId)) {
        const level = normalizeLevel(ev.level);
        if (counts[level] !== undefined) {
          counts[level]++;
        }
      }
    });

    return counts;
  };

  // Extract numeric grade from grade_level string (e.g., "Grade 6" -> 6, "K" -> -1, "6" -> 6)
  const extractGradeNumber = (gradeLevel) => {
    if (!gradeLevel) return null;
    const lower = String(gradeLevel).toLowerCase().trim();
    
    // Handle K/Kindergarten
    if (lower === 'k' || lower === 'kindergarten' || lower.includes('kindergarten')) {
      return -1;
    }
    
    // Look for a number in the string
    const match = lower.match(/(\d+)/);
    if (match) {
      return parseInt(match[1]);
    }
    
    return null;
  };

  const getEvidenceGradeNumbers = (ev) => {
    const gradeNumbers = [];

    if (Array.isArray(ev.grades)) {
      ev.grades.forEach((grade) => {
        const gradeNumber = extractGradeNumber(grade);
        if (gradeNumber !== null) gradeNumbers.push(gradeNumber);
      });
    } else if (typeof ev.grades === 'string' && ev.grades.trim()) {
      try {
        const parsedGrades = JSON.parse(ev.grades);
        if (Array.isArray(parsedGrades)) {
          parsedGrades.forEach((grade) => {
            const gradeNumber = extractGradeNumber(grade);
            if (gradeNumber !== null) gradeNumbers.push(gradeNumber);
          });
        }
      } catch (error) {
        const gradeNumber = extractGradeNumber(ev.grades);
        if (gradeNumber !== null) gradeNumbers.push(gradeNumber);
      }
    }

    if (gradeNumbers.length === 0) {
      const fallbackGrade = extractGradeNumber(ev.grade_level);
      if (fallbackGrade !== null) gradeNumbers.push(fallbackGrade);
    }

    return gradeNumbers;
  };

  // For category display: Get evidence counts filtered by grade level span
  const getEvidenceCountsForSpan = (objectiveId, spanIndex) => {
    const counts = {
      Explore: 0,
      Apply: 0,
      'Independently Implement': 0
    };

    if (!evidence || !categorySpans[spanIndex]) return counts;

    const span = categorySpans[spanIndex];
    
    // Filter evidence for this objective that falls within the span's grade range
    evidence.forEach(ev => {
      if (objectiveIdMatches(ev.objective_id, objectiveId)) {
        const gradeNumbers = getEvidenceGradeNumbers(ev);

        // Check if any class grade falls within the span's range
        if (span.min !== undefined && span.max !== undefined) {
          const inSpan = gradeNumbers.some((gradeNumber) => gradeNumber >= span.min && gradeNumber <= span.max);
          if (inSpan) {
            const level = normalizeLevel(ev.level);
            if (counts[level] !== undefined) {
              counts[level]++;
            }
          }
        }
      }
    });

    return counts;
  };

  const getEvidenceIndicator = (count, level) => {
    if (count === 0) {
      return null;
    }
    const levelClass = String(level || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
    return (
      <span className={`evidence-indicator evidence-${levelClass}`}>
        {level.charAt(0)}<span className="evidence-count">({count})</span>
      </span>
    );
  };

  const renderObjectives = () => {
    if (!grades || grades.length === 0) {
      return <div className="empty-state"><p>No objectives available.</p></div>;
    }

    // For category display, group objectives by gradeband (category) with E/A/I per span
    if (displayType === 'category' && categorySpans.length > 0) {
      return (
        <div className="summary-objectives category-display">
          <div className="category-header-row category-grid-row">
            <div className="objective-label-col">Objectives</div>
            <div className="category-spans-cols category-spans-track">
              {categorySpans.map((span, idx) => (
                <div key={`span-header-${idx}`} className="category-span-col">
                  <div className="span-label">{span.label}</div>
                </div>
              ))}
            </div>
          </div>

          {grades.map(gradeband => {
            return (
              <div key={gradeband.id} className="gradeband-section category-section">
                <div className="gradeband-header">
                  <h3>{gradeband.title}</h3>
                  {gradeband.description && <p>{gradeband.description}</p>}
                </div>

                {gradeband.outcomes && gradeband.outcomes.length > 0 ? (
                  gradeband.outcomes.map(outcome => (
                    <div key={outcome.id} className="outcome-section">
                      <div className="outcome-header">
                        <h4>Outcome {outcome.number}: {outcome.title}</h4>
                      </div>
                      <div className="objectives-list category-display-list">
                        {outcome.objectives && outcome.objectives.length > 0 ? (
                          outcome.objectives.map(objective => (
                            <div key={objective.id} className="objective-row category-row category-grid-row">
                              <div className="objective-label-col">
                                <div className="objective-label">
                                  <strong>O{objective.number}:</strong> {objective.text}
                                </div>
                              </div>
                              <div className="category-spans-cols category-spans-track">
                                {categorySpans.map((span, idx) => {
                                  const counts = getEvidenceCountsForSpan(objective.id, idx);
                                  return (
                                    <div key={`${objective.id}-span-${idx}`} className="category-span-col">
                                      <div className="evidence-indicators">
                                        {getEvidenceIndicator(counts.Explore, 'Explore')}
                                        {getEvidenceIndicator(counts.Apply, 'Apply')}
                                        {getEvidenceIndicator(counts['Independently Implement'], 'Independently Implement')}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-objectives">No objectives in this outcome.</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-objectives">No outcomes in this category.</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Standard display: show regular gradebands with outcomes
    return (
      <div className="summary-objectives">
        {grades.map(gradeband => {
          return (
            <div key={gradeband.id} className="gradeband-section">
              <div className="gradeband-header">
                <h3>{gradeband.title}</h3>
                {gradeband.description && <p>{gradeband.description}</p>}
              </div>

              {gradeband.outcomes && gradeband.outcomes.length > 0 ? (
                gradeband.outcomes.map(outcome => (
                  <div key={outcome.id} className="outcome-section">
                    <div className="outcome-header">
                      <h4>Outcome {outcome.number}: {outcome.title}</h4>
                    </div>
                    <div className="objectives-list">
                      {outcome.objectives && outcome.objectives.length > 0 ? (
                        outcome.objectives.map(objective => {
                          const counts = getEvidenceCounts(objective.id);
                          return (
                            <div key={objective.id} className="objective-row">
                              <div className="objective-label">
                                <strong>O{objective.number}:</strong> {objective.text}
                              </div>
                              <div className="evidence-indicators">
                                {getEvidenceIndicator(counts.Explore, 'Explore')}
                                {getEvidenceIndicator(counts.Apply, 'Apply')}
                                {getEvidenceIndicator(counts['Independently Implement'], 'Independently Implement')}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-objectives">No objectives in this outcome.</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-objectives">No outcomes in this band.</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="summary-section">
      <h2>Objectives Summary</h2>
      {renderObjectives()}
    </div>
  );
}

export default SummaryTabUser;

